import { useMemo, useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useCatalogScope } from "@/hooks/useCatalogScope";
import { useCanEditRecipes } from "@/hooks/useCanEditRecipes";
import type { useRecipeDialogs } from "@/hooks/useRecipeDialogs";
import {
  useCreateRecipe,
  useBulkDeleteRecipes,
  useBulkUpdateRecipeStatus,
  useDeleteRecipe,
} from "@/hooks/recipes";
import { recipeRequests } from "@/lib/api/requests/recipes";
import { menuItemRequests } from "@/lib/api/requests/menu-items";
import { adminApi } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CatalogScopeFilters } from "@/components/common/CatalogScopeFilters";
import { ApiDatatable } from "@/components/data-table";
import {
  TabBar,
  HighlightBanner,
  RecipesSectionHeader,
} from "@/components/recipes";
import {
  CreateNewDialog,
  ExportRecipesDialog,
  ImportRecipesDialog,
  ImportIngredientsDialog,
  ShareRecipesCatalogDialog,
  CreateRecipeDialog,
  CreatePrepRecipeDialog,
  ChangeToDraftDialog,
  DeleteSelectedDialog,
} from "@/components/recipes/dialogs";
import {
  toPrepRecipeRows,
  getPrepRecipeColumns,
  getMenuItemExportColumns,
  getPrepRecipeExportColumns,
  getIngredientExportColumns,
  PrepRecipeTile,
} from "@/components/recipes/tables";
import { exportRecipesToFile } from "@/lib/export/recipes-excel";
import { MaterialsTabContent } from "@/components/materials";
import type { RecipeType } from "@/types/recipe";
import { ActiveTabsIds } from "@/components/recipes/tabs/types";
import { getAddRecipeDialog, getTabs } from "@/components/recipes/tabs/tab-utils";

type RecipesSearchParams = {
  tab: ActiveTabsIds;
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  status: "all" | "draft" | "published" | "archived";
  ingredientStatus?: "all" | "active" | "inactive";
  /** Unset = both kinds. The Recipes tab holds prep and plated alike (T-701). */
  recipeType?: RecipeType;
};

export type RecipesListingSearchParams = RecipesSearchParams;

interface RecipesListingProps {
  searchParams: RecipesSearchParams;
  onNavigate: (updater: (prev: RecipesSearchParams) => RecipesSearchParams) => void;
  dialogs: ReturnType<typeof useRecipeDialogs>;
  /** When set, pre-filters to recipes created by this user. */
  createdById?: number;
  /** When true, hides the banner and section header — shows only the tab bar and tables. */
  embedded?: boolean;
}

const toSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export function RecipesListing({ searchParams, onNavigate, dialogs, createdById, embedded }: RecipesListingProps) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const catalogScope = useCatalogScope();
  // Recipes are edited in Build mode only; Working mode is view-only (YC-33).
  const canEditRecipes = useCanEditRecipes();

  const {
    isAuthenticated,
    isInternalRecipeViewer,
    showRecipeOrgColumn,
    showRecipeCompanyColumn,
    fixedOrganizationId,
    effectiveOrganizationId,
    resolvedFilterCompanyId,
    isInternalScopeReady,
    dialogFixedCompanyId,
    dialogDefaultCompanyId,
    organizationNamesById,
  } = catalogScope;

  const [isSelecting, setIsSelecting] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [isExporting, setIsExporting] = useState(false);

  // Always-on lightweight queries (limit=1) so counts are ready before the user
  // clicks every tab. Re-run whenever the active search/filter params change.
  const countParams = useMemo(() => ({
    createdById,
    companyId: resolvedFilterCompanyId ?? undefined,
    search: searchParams.search,
    recipeType: searchParams.recipeType,
    isPublished:
      searchParams.status === "published"
        ? true
        : searchParams.status === "draft"
          ? false
          : undefined,
    isActive: searchParams.status === "archived" ? false : undefined,
    page: 1,
    limit: 1,
  }), [
    createdById,
    resolvedFilterCompanyId,
    searchParams.search,
    searchParams.recipeType,
    searchParams.status,
  ]);

  // Prep recipes are recipe records; menu items are their own entity.
  const { data: recipesTotalData } = useQuery({
    queryKey: ["recipes", "count", countParams],
    queryFn: () => recipeRequests.list(countParams),
    enabled: isAuthenticated && isInternalScopeReady,
    staleTime: 30_000,
  });
  const { data: menuItemsTotalData } = useQuery({
    queryKey: ["menu-items", "count", searchParams.search, resolvedFilterCompanyId ?? null],
    queryFn: () =>
      menuItemRequests.list({
        page: 1,
        limit: 1,
        search: searchParams.search,
        companyId: resolvedFilterCompanyId ?? undefined,
      }),
    enabled: isAuthenticated && isInternalScopeReady,
    staleTime: 30_000,
  });
  const { data: ingredientsTotalData } = useQuery({
    queryKey: ["admin", "materials", "count"],
    queryFn: () => adminApi.getMaterials({ page: 1, limit: 1 }),
    enabled: isAuthenticated && isInternalScopeReady,
    staleTime: 30_000,
  });

  const menuItemsTotal = menuItemsTotalData?.meta.total ?? 0;
  const prepRecipesTotal = recipesTotalData?.meta.total ?? 0;
  const ingredientsTotal = ingredientsTotalData?.meta.total ?? 0;

  // Selected recipe IDs are stored as row keys (strings) — convert to numbers.
  const selectedPrepRecipeIds = useMemo(
    () =>
      searchParams.tab === "prepRecipes"
        ? Object.entries(rowSelection)
            .filter(([, v]) => v)
            .map(([k]) => Number(k))
        : [],
    [rowSelection, searchParams.tab],
  );

  const menuItemExportColumns = useMemo(
    () => getMenuItemExportColumns(i18n),
    [i18n],
  );
  const prepRecipeExportColumns = useMemo(
    () => getPrepRecipeExportColumns(i18n),
    [i18n],
  );
  const ingredientExportColumns = useMemo(
    () => getIngredientExportColumns(i18n),
    [i18n],
  );

  const queryClient = useQueryClient();

  const createRecipe = useCreateRecipe();
  const bulkDelete = useBulkDeleteRecipes();
  const bulkUpdateStatus = useBulkUpdateRecipeStatus();
  const deleteRecipe = useDeleteRecipe();

  const setTab = (tab: ActiveTabsIds) =>
    onNavigate((prev) => ({
      ...prev,
      tab,
      page: 1,
      recipeType: undefined,
      search: undefined,
    }));

  const prepRecipeColumns = useMemo(
    () =>
      getPrepRecipeColumns(i18n, {
        selectable: isSelecting,
        readOnly: !canEditRecipes,
        showOrganization: showRecipeOrgColumn,
        showCompany: showRecipeCompanyColumn,
        callbacks: {
          onEdit: (row) =>
            navigate({ to: "/recipes/$recipeId", params: { recipeId: String(row.id) } }),
          onDelete: (row) =>
            deleteRecipe.mutate(Number(row.id), {
              onSuccess: () => toast.success(t`Recipe deleted`),
              onError: () => toast.error(t`Failed to delete recipe`),
            }),
        },
      }),
    [
      i18n,
      isSelecting,
      canEditRecipes,
      showRecipeOrgColumn,
      showRecipeCompanyColumn,
      deleteRecipe,
      navigate,
      t,
    ],
  );

  const recipeTypeToolbar = (
    <select
      aria-label={t`Filter by recipe type`}
      value={searchParams.recipeType ?? "all"}
      onChange={(e) =>
        onNavigate((prev) => ({
          ...prev,
          // "all" is the absence of a filter, not a third kind of recipe.
          recipeType: e.target.value === "all" ? undefined : (e.target.value as RecipeType),
          page: 1,
        }))
      }
      className="h-9 w-36 rounded-md border border-(--color-border) bg-transparent px-3 text-sm"
    >
      <option value="all">{t`All types`}</option>
      <option value="PREP">{t`Prep recipes`}</option>
      <option value="PLATED">{t`Plated recipes`}</option>
    </select>
  );

  const statusToolbar = (
    <Select
      value={searchParams.status}
      onValueChange={(status) =>
        onNavigate((prev) => ({
          ...prev,
          status: status as RecipesSearchParams["status"],
          page: 1,
        }))
      }
    >
      <SelectTrigger className="w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t`All statuses`}</SelectItem>
        <SelectItem value="draft">{t`Draft`}</SelectItem>
        <SelectItem value="published">{t`Published`}</SelectItem>
        <SelectItem value="archived">{t`Archived`}</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <>
      <TabBar tabs={getTabs(i18n)} active={searchParams.tab} onChange={setTab} />

      {!embedded && (
        <HighlightBanner
          title={t`Featured Recipe of the Week`}
          subtitle={t`Chef's pick`}
        />
      )}

      {!embedded && (
        <RecipesSectionHeader
          activeTab={searchParams.tab}
          isSelecting={isSelecting}
          showAdd={canEditRecipes || searchParams.tab === "ingredients"}
          onSelectClick={() => {
            setIsSelecting((prev) => !prev);
            setRowSelection({});
          }}
          onAddClick={() => {
            if (searchParams.tab === "prepRecipes") {
              // The Recipes tab holds prep and plated alike, so Add must not
              // assume prep. When the list is already filtered to one kind the
              // answer is known; otherwise ask via the Create-new chooser.
              const dialogId = getAddRecipeDialog(searchParams.recipeType);
              dialogs.open(dialogId);
              return;
            }
            dialogs.open("createMaterial");
          }}
        />
      )}

      {!embedded && <CatalogScopeFilters scope={catalogScope} />}

      {!embedded && isInternalRecipeViewer && !isInternalScopeReady && (
        <div className="mb-3 rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-2 text-sm text-(--color-text-secondary)">
          {t`Select organization and concept to load recipe dashboard data.`}
        </div>
      )}

      {searchParams.tab === "prepRecipes" && (
        <ApiDatatable
          searchParams={searchParams}
          onNavigate={onNavigate}
          queryKey={["recipes", "prepRecipes", resolvedFilterCompanyId ?? null]}
          queryFn={(params) =>
            recipeRequests
              .list({
                createdById,
                page: params.page,
                limit: params.pageSize,
                search: params.search,
                sortBy: params.sortBy as never,
                sortOrder: params.sortOrder,
                companyId: resolvedFilterCompanyId ?? undefined,
                recipeType: params.recipeType,
                include: ["company"],
                isPublished:
                  params.status === "published"
                    ? true
                    : params.status === "draft"
                      ? false
                      : undefined,
                isActive: params.status === "archived" ? false : undefined,
              })
              .then((res) => ({ data: toPrepRecipeRows(res.data, organizationNamesById, i18n), meta: res.meta }))
          }
          columns={prepRecipeColumns}
          renderTile={(item) => (
            <PrepRecipeTile
              item={item}
              onClick={isSelecting ? undefined : () =>
                navigate({ to: "/recipes/$recipeId", params: { recipeId: String(item.id) } })
              }
            />
          )}
          getRowId={(row) => String(row.id)}
          onRowClick={isSelecting ? undefined : (row) =>
            navigate({ to: "/recipes/$recipeId", params: { recipeId: String(row.id) } })
          }
          toolbarExtra={
            <>
              {recipeTypeToolbar}
              {statusToolbar}
            </>
          }
          searchPlaceholder={t`Search recipes…`}
          emptyMessage={
            isInternalRecipeViewer && !isInternalScopeReady
              ? t`Select organization and concept to view prep recipes`
              : t`No prep recipes found`
          }
          enabled={isAuthenticated && isInternalScopeReady}
          enableRowSelection={isSelecting}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
        />
      )}

      {searchParams.tab === "ingredients" && (
        <MaterialsTabContent
          searchParams={searchParams}
          onNavigate={onNavigate}
          createOpen={dialogs.isOpen("createMaterial")}
          onCreateOpenChange={dialogs.setDialogOpen("createMaterial")}
          enabled={isAuthenticated && isInternalScopeReady}
        />
      )}

      <CreateNewDialog
        open={dialogs.isOpen("createNew")}
        onOpenChange={dialogs.setDialogOpen("createNew")}
        onSelect={(type) => {
          if (type === "platedRecipe") dialogs.open("createRecipe");
          else if (type === "prepRecipe") dialogs.open("createPrepRecipe");
          else if (type === "ingredient") {
            setTab("ingredients");
            dialogs.open("createMaterial");
          }
        }}
      />

      <ExportRecipesDialog
        open={dialogs.isOpen("exportRecipes")}
        onOpenChange={dialogs.setDialogOpen("exportRecipes")}
        onExport={(data) => {
          setIsExporting(true);
          const baseParams = {
            search: searchParams.search,
            isPublished:
              searchParams.status === "published"
                ? true
                : searchParams.status === "draft"
                  ? false
                  : undefined,
            isActive: searchParams.status === "archived" ? false : undefined,
          };
          exportRecipesToFile({
            includeMenuItems: data.includeMenuItems,
            menuItemColumns: menuItemExportColumns,
            includePrepRecipes: data.includePrepRecipes,
            prepRecipeColumns: prepRecipeExportColumns,
            includeIngredients: data.includeIngredients ?? false,
            ingredientColumns: ingredientExportColumns,
            fileType: data.fileType,
            selectedMenuItemIds: data.selectedMenuItemIds,
            selectedPrepRecipeIds: data.selectedPrepRecipeIds,
            baseParams,
          })
            .then(() => {
              dialogs.close();
              toast.success(t`Selected items have been exported`);
            })
            .catch(() => toast.error(t`Export failed`))
            .finally(() => setIsExporting(false));
        }}
        menuItemsCount={menuItemsTotal}
        prepRecipesCount={prepRecipesTotal}
        ingredientsCount={ingredientsTotal}
        isPending={isExporting}
        selectedPrepRecipeIds={selectedPrepRecipeIds}
      />

      <CreateRecipeDialog
        open={dialogs.isOpen("createRecipe")}
        onOpenChange={dialogs.setDialogOpen("createRecipe")}
        onConfirm={(data, companyId) => {
          createRecipe.mutate(
            {
              name: data.name,
              slug: toSlug(data.name),
              recipeType: data.recipeType,
              // The API takes a yield as a pair; half of one is dropped.
              ...(data.totalYield != null && data.totalYield > 0 && data.yieldUnit
                ? { yieldQuantity: data.totalYield, yieldUom: data.yieldUnit }
                : {}),
              companyId,
            },
            {
              onSuccess: (recipe) => {
                toast.success(t`Recipe created`);
                dialogs.close();
                navigate({ to: "/recipes/$recipeId", params: { recipeId: recipe.id.toString() } });
              },
              onError: () => toast.error(t`Failed to create recipe`),
            },
          );
        }}
        companyId={dialogFixedCompanyId}
        defaultCompanyId={dialogDefaultCompanyId}
        organizationId={effectiveOrganizationId}
        canSelectOrganization={isInternalRecipeViewer && !fixedOrganizationId}
        isPending={createRecipe.isPending}
        // Only reached from "Add plated recipe"; prep recipes have their own
        // dialog below.
        defaultRecipeType="PLATED"
      />

      <CreatePrepRecipeDialog
        open={dialogs.isOpen("createPrepRecipe")}
        onOpenChange={dialogs.setDialogOpen("createPrepRecipe")}
        onSave={(data, companyId) => {
          createRecipe.mutate(
            {
              name: data.name,
              slug: toSlug(data.name),
              // This dialog only ever makes components, so it says so rather
              // than asking a question whose answer it already knows.
              recipeType: "PREP",
              companyId,
              servings: data.servings,
              prepTimeMinutes: data.prepTimeMinutes,
              cookTimeMinutes: data.cookTimeMinutes,
              // The API takes a yield as a pair; half of one is dropped.
              ...(data.totalYield != null && data.totalYield > 0 && data.yieldUnit
                ? { yieldQuantity: data.totalYield, yieldUom: data.yieldUnit }
                : {}),
            },
            {
              onSuccess: (recipe) => {
                toast.success(t`Prep recipe created`);
                dialogs.close();
                navigate({ to: "/recipes/$recipeId", params: { recipeId: recipe.id.toString() } });
              },
              onError: () => toast.error(t`Failed to create recipe`),
            },
          );
        }}
        companyId={dialogFixedCompanyId}
        defaultCompanyId={dialogDefaultCompanyId}
        organizationId={effectiveOrganizationId}
        canSelectOrganization={isInternalRecipeViewer && !fixedOrganizationId}
        isPending={createRecipe.isPending}
      />

      <ChangeToDraftDialog
        open={dialogs.isOpen("changeToDraft")}
        onOpenChange={dialogs.setDialogOpen("changeToDraft")}
        onConfirm={() => {
          bulkUpdateStatus.mutate(
            { ids: [], isPublished: false },
            {
              onSuccess: () => {
                toast.success(t`Recipes changed to draft`);
                dialogs.close();
              },
              onError: () => toast.error(t`Failed to update recipes`),
            },
          );
        }}
        isPending={bulkUpdateStatus.isPending}
      />

      <DeleteSelectedDialog
        open={dialogs.isOpen("deleteSelected")}
        onOpenChange={dialogs.setDialogOpen("deleteSelected")}
        onConfirm={() => {
          bulkDelete.mutate([], {
            onSuccess: () => {
              toast.success(t`Recipes deleted`);
              dialogs.close();
            },
            onError: () => toast.error(t`Failed to delete recipes`),
          });
        }}
        selectedCount={0}
        isPending={bulkDelete.isPending}
      />

      <ImportRecipesDialog
        open={dialogs.isOpen("importRecipes")}
        onOpenChange={dialogs.setDialogOpen("importRecipes")}
        onImportComplete={() => {
          void queryClient.invalidateQueries({ queryKey: ["recipes", "menuItems"] });
          void queryClient.invalidateQueries({ queryKey: ["recipes", "prepRecipes"] });
          toast.success(t`Import complete`);
        }}
      />

      <ImportIngredientsDialog
        open={dialogs.isOpen("importIngredients")}
        onOpenChange={dialogs.setDialogOpen("importIngredients")}
        onImportComplete={() => {
          void queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
        }}
      />

      <ShareRecipesCatalogDialog
        open={dialogs.isOpen("shareRecipes")}
        onOpenChange={dialogs.setDialogOpen("shareRecipes")}
      />
    </>
  );
}
